<template>
  <!-- Main Content Area -->
  <div class="d-flex align-center justify-space-between">
    <!-- Top tabs -->
    <v-tabs v-model="currentTab" color="primary" class="d-flex justify-space-around">
      <v-tab value="pages">
        <v-icon start class="mr-0 mr-sm-2">mdi-view-dashboard</v-icon>
        <span class="d-none d-sm-inline">Pages</span>
      </v-tab>
      <v-tab value="global_settings">
        <v-icon start class="mr-0 mr-sm-2">mdi-cog</v-icon>
        <span class="d-none d-sm-inline">Global Settings</span>
      </v-tab>
      <v-tab value="help">
        <v-icon start class="mr-0 mr-sm-2">mdi-help-circle</v-icon>
        <span class="d-none d-sm-inline">Help</span>
      </v-tab>
    </v-tabs>
  </div>

  <div class="mt-4 d-flex align-center pa-2" v-if="showFilters && selectedPage === null">
    <div class="w-100 d-flex ga-2 justify-space-between" v-if="currentTab === 'pages'">
      <CopyToClipboard
        label="embed code"
        :content-to-copy="`<script src='${cdnUrl}/lightning-model-page/cx/loader.js'><\/script><div class='lightning-model-page' data-account-id='${accountSettings.accountId}'></div>`"
      />
      <v-btn color="primary" @click="showAddDialog = true">
        <v-icon>mdi-plus</v-icon>
        <span class="d-none d-md-block">Add New</span>
      </v-btn>
    </div>
  </div>

  <!-- Main content area controlled by top tabs -->
  <v-window v-model="currentTab" :touch="false">
    <v-window-item value="pages">
      <v-card v-if="selectedPage === null">
        <!-- Model pages table -->
        <v-container>
          <v-row>
            <v-col cols="12">
              <v-card>
                <v-card-text>
                  <v-data-table
                    :headers="tableHeaders"
                    :items="tableData || []"
                    :items-per-page="5"
                    :loading="productSettings.loading"
                    height="400px"
                    fixed-header
                    hover
                  >
                    <template v-slot:item="{ item }">
                      <tr @click="handleRowClick(item)" class="cursor-pointer">
                        <td>{{ item.model }}</td>
                        <td>{{ item.path }}</td>
                        <td>{{ item.loadCount }}</td>
                        <td>{{ item.clickCount }}</td>
                        <td>{{ item.ctr }}</td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card>

      <!-- Edit model page -->
      <v-container v-else>
        <v-row>
          <v-col
            cols="12"
            class="d-flex justify-space-between align-center flex-wrap flex-sm-nowrap w-100"
          >
            <!-- Header -->
            <div class="align-center d-flex w-100 justify-start">
              <v-icon
                color="secondary"
                @click="handleBack"
                :disabled="productSettings.saving"
                size="36"
              >
                mdi-keyboard-backspace
              </v-icon>
              <h2>
                {{ `${selectedPage.year} ${selectedPage.make} ${selectedPage.model}` }}
              </h2>
            </div>

            <!-- Action buttons -->
            <div class="d-flex justify-space-between justify-sm-end ga-5 w-100">
              <v-btn variant="flat" color="success" @click="previewModelPage">
                <v-icon>mdi-eye</v-icon>
                <span class="d-none d-sm-block">Preview</span>
              </v-btn>
              <v-btn
                variant="flat"
                color="error"
                @click="removePage(selectedPage)"
                :disabled="productSettings.saving"
              >
                <v-icon>mdi-delete</v-icon>
                <span class="d-none d-sm-block">Delete</span>
              </v-btn>
              <v-btn
                variant="flat"
                color="primary"
                @click="savePage"
                :disabled="productSettings.saving || !hasChanges"
              >
                <v-icon>mdi-content-save</v-icon>
                <span class="d-none d-sm-block">Save</span>
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <div class="d-flex justify-space-between align-center flex-wrap flex-sm-nowrap">
              <div class="d-flex align-center ga-2 flex-wrap flex-sm-nowrap">
                <CopyToClipboard
                  label="path"
                  :content-to-copy="`/models/${selectedPage.year}/${selectedPage.make}/${selectedPage.model}`"
                />
                <CopyToClipboard
                  label="url hash"
                  :content-to-copy="`/models#${selectedPage.year}/${selectedPage.make}/${selectedPage.model}`"
                />
              </div>
              <v-switch label="Status" v-model="selectedPage.status" color="primary" hide-details />
            </div>
          </v-col>

          <v-col cols="12">
            <v-row>
              <!-- Left vertical menu -->
              <v-col cols="12" md="4" lg="3">
                <v-card>
                  <v-list nav density="comfortable">
                    <v-list-item
                      @click="activeSection = 'primary_cta'"
                      :active="activeSection === 'primary_cta'"
                      prepend-icon="mdi-button-pointer"
                      title="Primary CTA"
                    ></v-list-item>

                    <v-list-item
                      @click="activeSection = 'secondary_cta'"
                      :active="activeSection === 'secondary_cta'"
                      prepend-icon="mdi-button-cursor"
                      title="Secondary CTA"
                    ></v-list-item>

                    <v-list-item
                      @click="activeSection = 'highlighted_features'"
                      :active="activeSection === 'highlighted_features'"
                      prepend-icon="mdi-star"
                      title="Highlighted Features"
                    ></v-list-item>

                    <!-- You can add more sections here as needed -->
                  </v-list>
                </v-card>
              </v-col>

              <!-- Main content area -->
              <v-col cols="12" md="8" lg="9">
                <!-- Content based on selected section -->
                <v-fade-transition mode="out-in">
                  <!-- Primary CTA Section -->
                  <v-card v-if="activeSection === 'primary_cta'" key="primary_cta">
                    <v-card-title>Primary CTA</v-card-title>
                    <v-card-text>
                      <v-checkbox
                        v-model="overrideGlobalPrimaryCTA"
                        label="Override Global Primary CTA"
                      />
                      <div v-if="overrideGlobalPrimaryCTA">
                        <CTAPreview :settings="selectedPage.primaryCTA" />
                        <CTASettings v-model="selectedPage.primaryCTA" />
                      </div>
                    </v-card-text>
                  </v-card>

                  <!-- Secondary CTA Section -->
                  <v-card v-else-if="activeSection === 'secondary_cta'" key="secondary_cta">
                    <v-card-title>Secondary CTA</v-card-title>
                    <v-card-text>
                      <v-checkbox
                        v-model="overrideGlobalSecondaryCTA"
                        label="Override Global Secondary CTA"
                      />
                      <div v-if="overrideGlobalSecondaryCTA">
                        <CTAPreview :settings="selectedPage.secondaryCTA" />
                        <CTASettings v-model="selectedPage.secondaryCTA" />
                      </div>
                    </v-card-text>
                  </v-card>

                  <!-- Highlighted Features Section -->
                  <v-card
                    v-else-if="activeSection === 'highlighted_features'"
                    key="highlighted_features"
                  >
                    <v-card-title>Highlighted Features Sections</v-card-title>
                    <v-card-text>
                      <HighlightedFeaturesSectionsGroupManager
                        v-model="selectedPage.highlightedFeatures"
                      />
                    </v-card-text>
                  </v-card>
                </v-fade-transition>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </v-window-item>

    <!-- Trim dashboard -->
    <v-window-item value="trims">
      <v-card>
        <v-card-text>
          <div>Trims</div>
        </v-card-text>
      </v-card>
    </v-window-item>

    <!-- Global settings -->
    <v-window-item value="global_settings">
      <v-card>
        <v-card-text>
          <GlobalSettingsManager />
        </v-card-text>
      </v-card>
    </v-window-item>

    <!-- Help content -->
    <v-window-item value="help">
      <Help />
    </v-window-item>
  </v-window>

  <!-- Add new model page dialog -->
  <AddNewModelDialog v-model="showAddDialog" @add-new-page="addNewPage" />

  <!-- Delete model page dialog -->
  <v-dialog v-model="showDeleteDialog" max-width="500px">
    <v-card>
      <v-card-title>Delete Page</v-card-title>
      <v-card-text> Are you sure you want to delete this page? </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="text" @click="cancelDelete"> Cancel </v-btn>
        <v-btn color="error" @click="deleteSelectedPage"> Delete </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Confirm Discard Dialog -->
  <v-dialog v-model="showConfirmDialog" max-width="500px">
    <v-card>
      <v-card-title>Unsaved Changes</v-card-title>
      <v-card-text> You have unsaved changes. Are you sure you want to discard them? </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="text" @click="showConfirmDialog = false">
          Keep Editing
        </v-btn>
        <v-btn color="error" @click="confirmDiscard"> Discard Changes </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Snackbar -->
  <v-snackbar v-model="showSnackbar" :timeout="2000" :color="snackbarColor">
    {{ snackbarText }}

    <template #actions>
      <v-btn icon="mdi-close" @click="showSnackbar = false"></v-btn>
    </template>
  </v-snackbar>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import AddNewModelDialog from '@/components/AddNewModelDialog.vue'
import GlobalSettingsManager from '@/components/GlobalSettingsManager.vue'
import HighlightedFeaturesSectionsGroupManager from '@/components/HighlightedFeaturesSectionsGroupManager.vue'
import Help from '@/components/Help.vue'
import CopyToClipboard from '@/components/CopyToClipboard.vue'
import type { IPage, IProductSettings } from '@/types'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { useProductSettingsStore } from '@/stores/productSettings'
import {CTASettings, JazelAppConfig} from '@jazel/jazel-vuetify'
import CTAPreview from '@/components/CTAPreview.vue'

const cdnUrl = (JazelAppConfig as any)?.cdnUrl ?? ''

const activeSection = ref('primary_cta')

const tableHeaders = [
  {
    title: 'Model',
    key: 'model',
    align: 'start' as const,
    value: 'model',
  },
  {
    title: 'Path',
    key: 'path',
    align: 'start' as const,
    value: 'path',
  },
  { title: 'Loads', key: 'loads', align: 'start' as const, value: 'loadCount' },
  {
    title: 'Clicks',
    key: 'clicks',
    align: 'start' as const,
    value: 'clickCount',
  },
  { title: 'CTR', key: 'ctr', align: 'start' as const, value: 'ctr' },
]

const accountSettings = useAccountSettingsStore()
const productSettings = useProductSettingsStore()

const pages = computed(() => {
  return productSettings.settings?.pages ?? []
})
const filteredPages = ref<IPage[]>([])
const pageToDelete = ref<IPage | null>(null)
const selectedPage = ref<IPage | null>(null)
const pagesCount = ref(0)

const overrideGlobalPrimaryCTA = computed({
  get: () => selectedPage.value?.overrideGlobalPrimaryCTA ?? false,
  set: (value) => {
    if (selectedPage.value) {
      selectedPage.value.overrideGlobalPrimaryCTA = value
    }
  },
})

const overrideGlobalSecondaryCTA = computed({
  get: () => selectedPage.value?.overrideGlobalSecondaryCTA ?? false,
  set: (value) => {
    if (selectedPage.value) {
      selectedPage.value.overrideGlobalSecondaryCTA = value
    }
  },
})

const tableData = computed(() => {
  return filteredPages.value.map((page) => ({
    model: page.model,
    path: page.path,
    loadCount: 0,
    clickCount: 0,
    ctr: 0,
  }))
})

const currentTab = ref('pages')
const showAddDialog = ref(false)
const showDeleteDialog = ref(false)

const showSnackbar = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('success')

const showFilters = computed(() => {
  return currentTab.value !== 'help' && currentTab.value !== 'global_settings'
})

// Add new model page
const addNewPage = (newPage: IPage) => {
  if (productSettings.settings === null) {
    return
  }

  const newSettings: IProductSettings = {
    ...productSettings.settings,
    pages: productSettings.settings?.pages
      ? [...productSettings.settings?.pages, newPage]
      : [newPage],
  }

  productSettings
    .saveSettings(newSettings)
    .then(() => {
      showSnackbar.value = true
      snackbarText.value = 'New page added successfully'
      snackbarColor.value = 'success'

      showAddDialog.value = false

      pagesCount.value = productSettings.settings?.pages.length ?? 0
      filteredPages.value = [...filteredPages.value, newPage]
    })
    .catch((err) => {
      console.error(err)
      showSnackbar.value = true
      snackbarText.value = 'Failed to add new page'
      snackbarColor.value = 'error'
    })
}

const originalPage = ref<IPage | null>(null)
const pendingAction = ref<'back' | 'cancel' | null>(null)
const showConfirmDialog = ref<boolean>(false)

const hasChanges = computed(() => {
  if (!selectedPage.value || !originalPage.value) return false

  return JSON.stringify(selectedPage.value) !== JSON.stringify(originalPage.value)
})

const handleBack = () => {
  if (hasChanges.value) {
    pendingAction.value = 'back'
    showConfirmDialog.value = true
  } else {
    selectedPage.value = null
  }
}

const confirmDiscard = () => {
  showConfirmDialog.value = false
  pendingAction.value = null
  selectedPage.value = null
}

// Preview model page
const previewModelPage = () => {
  if (!selectedPage.value || !accountSettings.accountId) return

  const modelPageUrl = `${import.meta.env.VITE_CX_URL}?account_id=${accountSettings.accountId}#${selectedPage.value.year}/${selectedPage.value.make}/${selectedPage.value.model}`

  window.open(modelPageUrl, '_blank')
}

// Save model page settings
const savePage = () => {
  if (productSettings.settings === null) {
    return
  }

  const newSettings: IProductSettings = {
    ...productSettings.settings,
    pages:
      productSettings.settings?.pages.map((page) => {
        if (page.path === selectedPage.value?.path) {
          return selectedPage.value
        }
        return page
      }) ?? [],
  }

  productSettings
    .saveSettings(newSettings)
    .then(async () => {
      showSnackbar.value = true
      snackbarText.value = 'Page saved successfully'
      snackbarColor.value = 'success'

      const currentSection = activeSection.value

      // Verify that productSettings.fetchSettings is actually getting new data
      // and not using cached data
      originalPage.value = JSON.parse(JSON.stringify(selectedPage.value))
      pagesCount.value = productSettings.settings?.pages.length ?? 0

      activeSection.value = currentSection
    })
    .catch((err) => {
      console.error(err)
      showSnackbar.value = true
      snackbarText.value = 'Failed to save page'
      snackbarColor.value = 'error'
    })
}

// Remove model page
const removePage = (item: any) => {
  pageToDelete.value = pages.value.find((page) => page.path === item.path) ?? null
  showDeleteDialog.value = true
}

// Cancel delete model page
const cancelDelete = () => {
  pageToDelete.value = null
  showDeleteDialog.value = false
}

// Delete selected model page
const deleteSelectedPage = () => {
  if (pageToDelete.value === null) {
    return
  }

  if (!productSettings.settings?.globalSettings) {
    return
  }

  const newSettings: IProductSettings = {
    ...productSettings.settings,
    pages:
      productSettings.settings?.pages.filter((page) => page.path !== pageToDelete.value?.path) ??
      [],
  }

  productSettings
    .saveSettings(newSettings)
    .then(() => {
      showSnackbar.value = true
      snackbarText.value = 'Page deleted successfully'
      snackbarColor.value = 'success'

      showDeleteDialog.value = false
      selectedPage.value = null

      pagesCount.value = productSettings.settings?.pages.length ?? 0
      filteredPages.value = filteredPages.value.filter((p) => p.path !== pageToDelete.value?.path)
    })
    .catch((err) => {
      console.error(err)
      showSnackbar.value = true
      snackbarText.value = 'Failed to delete page'
      snackbarColor.value = 'error'
    })
}

// Model pages table row click handler
const handleRowClick = (item: any) => {
  console.log('pages', pages)
  const foundPage = pages.value.find((page) => page.path === item.path)
  selectedPage.value = foundPage ?? null
  originalPage.value = JSON.parse(JSON.stringify(selectedPage.value))
}

onMounted(async () => {
  console.log('Mounted Dashboard component', productSettings.settings)
  pagesCount.value = productSettings.settings?.pages.length ?? 0
  filteredPages.value = productSettings.settings?.pages ?? []
})

watch(selectedPage, (page: IPage | null) => {
  if (page) {
    console.log('Selected page:', page)
    if (selectedPage.value !== null) {
      activeSection.value = 'primary_cta'
    }

    if (!Array.isArray(page.highlightedFeatures)) {
      if (page.highlightedFeatures && typeof page.highlightedFeatures === 'object') {
        page.highlightedFeatures = [page.highlightedFeatures]
      } else {
        page.highlightedFeatures = []
      }
    }
  }
})
</script>

<style scoped>
:deep(.v-data-table-header__content > span) {
  font-weight: bold !important;
}
</style>
